import React from 'react';
import { Box, Typography, Chip, Grid, Paper } from '@mui/material';
import { Flight, LocationOn, AirplanemodeActive } from '@mui/icons-material';
import {
  COLORS,
  surfaceInsetSx,
  getAircraftGlassCardSx,
  getAircraftStatusAccent,
  isAircraftOperational
} from './clientPortalTheme';

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const ClientStatusHero = ({ aircraft, currentSector, activeCharter }) => {
  if (!aircraft) return null;

  const route = currentSector
    ? `${currentSector.departure?.airport || '—'} → ${currentSector.arrival?.airport || '—'}`
    : null;

  const operational = isAircraftOperational(aircraft.status);
  const accent = getAircraftStatusAccent(aircraft.status);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.5, md: 3 },
        mb: 3,
        ...getAircraftGlassCardSx(aircraft.status),
        boxShadow: `0 12px 28px ${accent}18, 0 6px 16px rgba(44, 38, 28, 0.07)`
      }}
    >
      <AirplanemodeActive
        sx={{
          position: 'absolute',
          right: -24,
          top: -24,
          fontSize: 160,
          opacity: 0.06,
          color: accent,
          transform: 'rotate(-18deg)'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: operational ? 'rgba(76, 175, 80, 0.08)' : 'rgba(229, 57, 53, 0.08)',
          bottom: -40,
          left: -20,
          pointerEvents: 'none'
        }}
      />

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} position="relative">
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
          <Box sx={{ width: 3, borderRadius: 2, bgcolor: accent, flexShrink: 0 }} />
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Flight sx={{ color: accent, fontSize: 28 }} />
              <Typography variant="h4" fontWeight={800} sx={{ color: COLORS.text }}>
                {aircraft.registration}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>
              {aircraft.manufacturer} {aircraft.model}
            </Typography>
            {activeCharter?.charterNumber && (
              <Chip
                label={`${activeCharter.charterNumber} — ${activeCharter.title}`}
                size="small"
                sx={{
                  mt: 1.5,
                  bgcolor: operational ? COLORS.greenGlass : COLORS.redGlass,
                  color: accent,
                  fontWeight: 600,
                  borderRadius: 999,
                  border: `1px solid ${operational ? 'rgba(61, 220, 132, 0.3)' : 'rgba(255, 107, 107, 0.3)'}`
                }}
              />
            )}
          </Box>
        </Box>
        <Chip
          label={aircraft.status?.replace(/_/g, ' ')}
          sx={{
            fontSize: '0.85rem',
            py: 2.2,
            px: 1.5,
            fontWeight: 700,
            bgcolor: operational ? COLORS.greenGlass : COLORS.redGlass,
            color: accent,
            borderRadius: 999,
            border: `1px solid ${operational ? 'rgba(61, 220, 132, 0.35)' : 'rgba(255, 107, 107, 0.35)'}`,
            boxShadow: `0 0 16px ${accent}33`
          }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 2.5 }} position="relative">
        {route && (
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                ...surfaceInsetSx,
                borderRadius: 3,
                borderLeft: `3px solid ${accent}`
              }}
            >
              <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Current Route
              </Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.text, mt: 0.5 }}>{route}</Typography>
              {currentSector?.departure?.date && (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                  Departure: {formatDate(currentSector.departure.date)} {currentSector.departure.time || ''}
                </Typography>
              )}
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={route ? 6 : 12}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              ...surfaceInsetSx,
              borderRadius: 3,
              borderLeft: `3px solid ${accent}`
            }}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <LocationOn sx={{ color: accent, fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: COLORS.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Location
              </Typography>
            </Box>
            <Typography fontWeight={600} sx={{ color: COLORS.text, mt: 0.5 }}>
              {[aircraft.currentLocation?.airport, aircraft.currentLocation?.hangar].filter(Boolean).join(' / ') || '—'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ClientStatusHero;
